import React from 'react'
import { Element } from 'react-bulma-components'
import { AvailableWorker } from '../../../api/users'
import { WorkersRequest, WorkersRequestPost } from '../../../api/workers-requests'
import { checkAvailibilityInPeriod, getWorkerAbsentLabel, getUserLabel } from '../../../utils/users'
import { compareDocsById } from '../../../utils/documents'
import { truncate } from 'lodash'
import { formatDate } from '../../../utils/date'

interface WorkerInputLabelProps {
  user: AvailableWorker
  post?: WorkersRequestPost
  workersRequest: WorkersRequest
  isDesiredWorker?: boolean
  lastMissionStartDate?: Date | null
}

/**
 * React component that generates a formatted label for a worker in the context of a specific post and workers request
 */
const WorkerInputLabel: React.FC<WorkerInputLabelProps> = ({
  user,
  post,
  workersRequest,
  isDesiredWorker,
  lastMissionStartDate,
}) => {
  const userLabel = getUserLabel(user)
  const isAvailableForWorkersRequest = checkAvailibilityInPeriod(
    new Date(workersRequest.missionsStart),
    new Date(workersRequest.missionsEnd),
    user.availability,
  )
  const absentLabel =
    !isAvailableForWorkersRequest && getWorkerAbsentLabel(user.availability, user, true)

  const laborCostCoefficientKind =
    user.laborCostCoefficients.find(coef => compareDocsById(coef.employer, workersRequest.employer))
      ?.kind === 'management'
      ? 'Gestion'
      : 'Délégation'

  const employerRelation = user.employerRelations.find(relation =>
    compareDocsById(relation.employer, workersRequest.employer),
  )

  const isEmployerCollaborationInactive = employerRelation?.collaboration?.isInactive

  const isDesired =
    isDesiredWorker ||
    (post && post.desiredWorkers?.some(desiredWorker => desiredWorker?._id === user._id))

  return (
    <Element renderAs="span">
      {userLabel}
      <Element renderAs="small"> - {laborCostCoefficientKind}</Element>
      <Element textSize={7}>
        {isDesired && <Element textColor="success">Désiré pour ce poste par l'EU</Element>}
        {isEmployerCollaborationInactive && (
          <Element textColor="danger">
            L'employeur a indiqué qu'il ne voulait plus travailler avec cet intérimaire
          </Element>
        )}
        {absentLabel && <Element textColor="warning">Absent {absentLabel.toLowerCase()}</Element>}
        {lastMissionStartDate && (
          <Element textColor="grey">
            Dernière mission pour ce poste le {formatDate(lastMissionStartDate)}
          </Element>
        )}
        {user.internalComment && (
          <Element>{truncate(user.internalComment, { length: 70 })}</Element>
        )}
        {user.internalInformation && (
          <Element>{truncate(user.internalInformation, { length: 70 })}</Element>
        )}
      </Element>
    </Element>
  )
}

export default WorkerInputLabel
